'use client'

import { useState, useEffect, useCallback, useLayoutEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Globe, Building2, FileText, Sparkles, ArrowRight, ArrowLeft, Plus, AlertCircle } from 'lucide-react'
import { StepIndicatorComponent } from './step-indicator'
import { useRouter } from 'next/navigation'
import { getSession } from "next-auth/react"
import Link from 'next/link'
import * as React from "react"
import { AddAccountForm } from './add-account-form'

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"

const steps = ['Basic Information', 'Select Pages']

interface Account {
  value: string;
  label: string;
}

interface AccountData {
  id: string;
  name: string;
  // Add other account fields if needed
}

interface Room {
  id: string;
  name: string;
  account: {
    id: string;
    name: string;
  };
  owner: {
    name: string;
  };
  createdAt: string;
  icon: string;
  iconBg: string;
  members: number;
  customerActivity: number;
  lastActivity: string;
  plays: number | 'Inactive';
}

interface CreateSmartRoomComponentProps {
  onClose: () => void;
  onRoomCreated: (newRoom: Room) => void;
  initialData?: {
    roomName: string;
    goals: string[];
    template: string;
    account: Account | null;
    selectedPages?: string[];
  };
}

interface Template {
  id: string;
  name: string;
  title: string;
  subtitle?: string;
  headerImage?: string;
  logo?: string;
  primaryColor?: string;
  secondaryColor?: string;
  goal?: string;
  tenantId?: string;
  pages?: Array<{
    id: string;
    title: string;
    components: Array<{
      id: string;
      componentType: string;
      config: Record<string, unknown>;
      order: number;
    }>;
  }>;
}

interface TemplatePage {
  id: string;
  title: string;
  visibility: string;
  type: string;
  order: number;
  global?: boolean;
  components: Array<{
    id: string;
    componentType: string;
    config: Record<string, unknown>;
    order: number;
  }>;
}

export function CreateSmartRoomComponent({ onClose, onRoomCreated, initialData }: CreateSmartRoomComponentProps) {
  const router = useRouter()

  const [step, setStep] = useState(1)
  const [formData, setFormData] = useState({
    roomName: initialData?.roomName || '',
    account: initialData?.account || null,
    template: initialData?.template || '',
    goals: [],
    headerTitle: '',
    headerSubtitle: '',
    primaryColor: '#3B82F6',
    secondaryColor: '#1E40AF',
    logo: null,
    headerImage: '/images/header3.jpg',
    selectedPages: initialData?.selectedPages || [] as string[]
  })

  const [isLoading, setIsLoading] = useState(false);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [accountOptions, setAccountOptions] = useState<Account[]>([]);
  const [accountPickerOpen, setAccountPickerOpen] = useState(false);
  const [accountPickerRect, setAccountPickerRect] = useState<{
    top: number;
    left: number;
    width: number;
    maxHeight: number;
  } | null>(null);
  const accountInputRef = useRef<HTMLInputElement>(null);

  const updateAccountPickerRect = useCallback(() => {
    const el = accountInputRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - 12;
    const spaceAbove = rect.top - 12;
    const openBelow = spaceBelow >= 140 || spaceBelow >= spaceAbove;
    const maxHeight = Math.min(240, Math.max(120, openBelow ? spaceBelow : spaceAbove));
    setAccountPickerRect({
      top: openBelow ? rect.bottom + 4 : rect.top - 4 - maxHeight,
      left: rect.left,
      width: rect.width,
      maxHeight,
    });
  }, []);
  const [templatePages, setTemplatePages] = useState<TemplatePage[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userTenantId, setUserTenantId] = useState<string | null>(null);
  const [showAddAccountForm, setShowAddAccountForm] = useState(false);
  const [noAccountsFound, setNoAccountsFound] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    account?: string;
    template?: string;
  }>({});
  const [error, setError] = useState<{ title: string; message: string } | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleSelectChange = (name: string, value: string) => {
    setFormData({ ...formData, [name]: value })
    // Clear validation error when template is selected
    if (name === 'template' && value) {
      setValidationErrors(prev => ({ ...prev, template: undefined }));
    }
  }

  const handleAccountChange = (selectedOption: Account | null) => {
    setFormData(prev => {
      // Set default room name if empty or still has default pattern
      const defaultRoomName = selectedOption
        ? `room for ${selectedOption.label}`
        : '';

      // Only update room name if it's empty or matches the previous default pattern
      const currentRoomName = prev.roomName;
      const previousDefaultPattern = prev.account
        ? `room for ${prev.account.label}`
        : '';

      const newRoomName = !currentRoomName || currentRoomName === previousDefaultPattern
        ? defaultRoomName
        : currentRoomName;

      return {
        ...prev,
        account: selectedOption,
        roomName: newRoomName
      };
    });
    setNoAccountsFound(false);
    // Clear validation error when account is selected
    if (selectedOption) {
      setValidationErrors(prev => ({ ...prev, account: undefined }));
    }
  }

  const handleAccountSearch = useCallback(async (query: string) => {
    try {
      const response = await fetch(`/api/accounts/search?query=${encodeURIComponent(query)}`);
      const data = await response.json();

      if (Array.isArray(data)) {
        const options = data.map((account: AccountData) => ({
          value: account.id,
          label: account.name
        }));
        setAccountOptions(options);
        setNoAccountsFound(options.length === 0 && isAdmin);
      } else {
        setAccountOptions([]);
        setNoAccountsFound(isAdmin);
      }
    } catch (error) {
      console.error('Error searching accounts:', error);
      setAccountOptions([]);
      setNoAccountsFound(isAdmin);
    }
  }, [isAdmin]);

  useEffect(() => {
    const checkUserRole = async () => {
      const session = await getSession();
      if (session?.user?.role === 'ADMIN') {
        setIsAdmin(true);
      }
      if (session?.user?.tenantId) {
        setUserTenantId(session.user.tenantId);
      }
    };
    checkUserRole();
  }, []);

  useEffect(() => {
    if (!accountPickerOpen) return;
    const timer = setTimeout(() => {
      handleAccountSearch(searchValue);
    }, searchValue.trim() ? 300 : 0);

    return () => clearTimeout(timer);
  }, [searchValue, accountPickerOpen, handleAccountSearch]);

  useLayoutEffect(() => {
    if (!accountPickerOpen) return;
    updateAccountPickerRect();
    const onReposition = () => updateAccountPickerRect();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [accountPickerOpen, updateAccountPickerRect]);

  // Set default room name when account is selected and room name is empty
  useEffect(() => {
    if (formData.account && !formData.roomName) {
      setFormData(prev => ({
        ...prev,
        roomName: `room for ${prev.account?.label || formData.account?.label}`
      }));
    }
  }, [formData.account?.value]);

  const nextStep = () => {
    // Validate required fields before proceeding
    if (step === 1) {
      const errors: { account?: string; template?: string } = {};

      if (!formData.account) {
        errors.account = 'Please select an account';
      }
      if (!formData.template) {
        errors.template = 'Please select a template';
      }

      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        return;
      }

      // Clear any validation errors if validation passes
      setValidationErrors({});
    }
    setStep(step + 1);
  }
  const prevStep = () => setStep(step - 1)

  const handleFinish = async () => {
    try {
      setError(null);
      setIsLoading(true);

      const session = await getSession();
      console.log("Client-side session:", session);

      if (!session || !session.user || !session.user.tenantId) {
        console.error('User not authenticated or missing tenantId');
        router.push('/login');
        return;
      }

      console.log('Sending request to create room:', formData);

      const response = await fetch('/api/rooms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          goals: [],
          selectedPages: formData.selectedPages,
          tenantId: session.user.tenantId
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = errorData.details || errorData.error || `HTTP error! status: ${response.status}`;
        throw new Error(errorMessage);
      }

      const data = await response.json() as Room;
      console.log('Room created successfully:', data);
      onRoomCreated(data);
      onClose();
    } catch (error) {
      console.error('Error creating room:', error);
      setError({
        title: 'Failed to create room',
        message: error instanceof Error ? error.message : 'An unexpected error occurred. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await fetch('/api/rooms?type=template');
        if (response.ok) {
          const data = await response.json();
          setTemplates(data);
        }
      } catch (error) {
        console.error('Error fetching templates:', error);
      }
    };

    fetchTemplates();
  }, []);

  useEffect(() => {
    const fetchTemplatePages = async (templateId: string) => {
      try {
        const response = await fetch(`/api/rooms/${templateId}/pages`);
        if (response.ok) {
          const pages = await response.json();
          setTemplatePages(pages);

          // Move default pages to selected section automatically
          const defaultPages = pages.filter((page: TemplatePage) =>
            page.type.toUpperCase() === 'DEFAULT'
          );
          const defaultPageIds = defaultPages.map((page: TemplatePage) => page.id);

          setFormData(prev => ({
            ...prev,
            selectedPages: defaultPageIds
          }));
        }
      } catch (error) {
        console.error('Error fetching template pages:', error);
      }
    };

    if (formData.template) {
      fetchTemplatePages(formData.template);
    } else {
      setTemplatePages([]);
      setFormData(prev => ({ ...prev, selectedPages: [] }));
    }
  }, [formData.template]);

  const handlePageToggle = (pageId: string) => {
    setFormData(prev => ({
      ...prev,
      selectedPages: prev.selectedPages.includes(pageId)
        ? prev.selectedPages.filter(id => id !== pageId)
        : [...prev.selectedPages, pageId]
    }));
  };

  const handleAccountCreated = async (accountData: {
    name: string;
    industry: string;
    website?: string;
    type: string;
    city: string;
    stateRegion: string;
    postalCode: string;
    numberOfEmployees: number;
    annualRevenue: number;
    timeZone: string;
    description: string;
    linkedInPage: string;
    logo: string;
  }) => {
    try {
      const response = await fetch('/api/accounts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(accountData),
      });

      if (response.ok) {
        const newAccount = await response.json();
        const newAccountOption = {
          value: newAccount.id,
          label: newAccount.name
        };
        // Update form data with the newly created account
        setFormData(prev => {
          // Set default room name if empty or still has default pattern
          const defaultRoomName = `room for ${newAccount.name}`;
          const currentRoomName = prev.roomName;
          const previousDefaultPattern = prev.account
            ? `room for ${prev.account.label}`
            : '';

          const newRoomName = !currentRoomName || currentRoomName === previousDefaultPattern
            ? defaultRoomName
            : currentRoomName;

          return {
            ...prev,
            account: newAccountOption,
            roomName: newRoomName
          };
        });
        setSearchValue(newAccount.name);
        setShowAddAccountForm(false);
        // Refresh account search to show the new account
        await handleAccountSearch(newAccount.name);
      }
    } catch (error) {
      console.error('Error creating account:', error);
    }
  };

  return (
    <>
      <AddAccountForm
        isOpen={showAddAccountForm}
        onClose={() => setShowAddAccountForm(false)}
        onSubmit={handleAccountCreated}
      />
      <div className="container mx-auto p-0">
        <Card className="w-full max-w-3xl mx-auto min-h-[600px] max-h-[85vh] flex flex-col bg-white shadow-2xl border-0">
          <CardHeader className="py-6 px-6 bg-gradient-to-r from-blue-50 to-indigo-50 shrink-0">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-600 rounded-lg">
                <Sparkles className="h-5 w-5 text-white" />
              </div>
              <CardTitle className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Create Smart Room
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="flex-grow flex flex-col overflow-hidden p-6">
            <div className="mb-3 shrink-0">
              <StepIndicatorComponent steps={steps} currentStep={step - 1} />
            </div>

            {error && (
              <Alert variant="destructive" className="mb-6 animate-in fade-in slide-in-from-top-2 duration-300">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>{error.title}</AlertTitle>
                <AlertDescription>
                  {error.message}
                </AlertDescription>
              </Alert>
            )}

            <div className="flex-grow overflow-y-auto">
              {step === 1 && (
                <div className="space-y-6">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <FileText className="h-4 w-4 text-blue-600" />
                      Room Name
                    </label>
                    <Input
                      placeholder="Enter room name..."
                      name="roomName"
                      value={formData.roomName}
                      onChange={handleInputChange}
                      className="h-11 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-blue-600" />
                        Account <span className="text-red-500">*</span>
                      </label>
                      {isAdmin && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowAddAccountForm(true)}
                          className="text-xs h-7 text-blue-600 hover:text-blue-700 hover:bg-blue-50 px-2"
                        >
                          <Plus className="h-3.5 w-3.5 mr-1" />
                          Create New Account
                        </Button>
                      )}
                    </div>
                    <div>
                      <input
                        ref={accountInputRef}
                        type="text"
                        className={`w-full h-11 px-4 text-sm rounded-lg border transition-all ${validationErrors.account
                          ? 'border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-100'
                          : 'border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100'
                          }`}
                        placeholder="Search or pick a company..."
                        value={searchValue}
                        onChange={(e) => {
                          setSearchValue(e.target.value);
                          setAccountPickerOpen(true);
                        }}
                        onFocus={() => {
                          setAccountPickerOpen(true);
                          updateAccountPickerRect();
                          if (!searchValue.trim()) {
                            void handleAccountSearch('');
                          }
                        }}
                        onBlur={() => {
                          setTimeout(() => setAccountPickerOpen(false), 150);
                        }}
                      />

                      {accountPickerOpen &&
                        accountPickerRect &&
                        typeof document !== 'undefined' &&
                        createPortal(
                          <div
                            className="fixed z-[80] bg-white rounded-lg border border-gray-200 shadow-xl overflow-hidden"
                            style={{
                              top: accountPickerRect.top,
                              left: accountPickerRect.left,
                              width: accountPickerRect.width,
                            }}
                            onMouseDown={(e) => e.preventDefault()}
                            onWheel={(e) => e.stopPropagation()}
                          >
                            <div className="px-4 py-2 text-xs font-medium text-gray-500 border-b">
                              {searchValue.trim() ? 'Matching companies' : 'Your companies'}
                            </div>
                            <div
                              className="overflow-y-auto overscroll-contain"
                              style={{ maxHeight: accountPickerRect.maxHeight }}
                            >
                              {accountOptions.length === 0 ? (
                                <p className="px-4 py-3 text-sm text-gray-500">
                                  {searchValue.trim()
                                    ? 'No companies match that name.'
                                    : 'No companies yet. Create one to attach this room.'}
                                </p>
                              ) : (
                                accountOptions.map((account) => (
                                  <div
                                    key={account.value}
                                    className="px-4 py-3 hover:bg-blue-50 cursor-pointer transition-colors text-sm border-b last:border-b-0"
                                    onClick={() => {
                                      handleAccountChange(account);
                                      setSearchValue('');
                                      setAccountPickerOpen(false);
                                    }}
                                  >
                                    {account.label}
                                  </div>
                                ))
                              )}
                            </div>
                          </div>,
                          document.body
                        )}

                      {formData.account && (
                        <div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-100">
                          <span className="text-xs font-medium text-blue-700">Selected: </span>
                          <span className="text-sm font-medium text-blue-900">{formData.account.label}</span>
                        </div>
                      )}

                      {validationErrors.account && (
                        <p className="mt-1 text-xs text-red-600">{validationErrors.account}</p>
                      )}
                      {noAccountsFound && searchValue.trim() && (
                        <div className="mt-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
                          <p className="text-sm text-amber-800 mb-2">No accounts found matching &quot;{searchValue}&quot;</p>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => setShowAddAccountForm(true)}
                              className="text-xs h-8 border-amber-300 text-amber-700 hover:bg-amber-100"
                            >
                              Create Account
                            </Button>
                            <Link href="/external-accounts">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="text-xs h-8 border-amber-300 text-amber-700 hover:bg-amber-100"
                              >
                                Import Account
                              </Button>
                            </Link>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <FileText className="h-4 w-4 text-blue-600" />
                      Template <span className="text-red-500">*</span>
                    </label>
                    <Select
                      value={formData.template}
                      onValueChange={(value) => handleSelectChange('template', value)}
                    >
                      <SelectTrigger className={`h-11 focus:ring-2 transition-all ${validationErrors.template
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-100'
                        : 'border-gray-300 focus:border-blue-500 focus:ring-blue-100'
                        }`}>
                        <SelectValue placeholder="Select a template..." />
                      </SelectTrigger>
                      <SelectContent>
                        {(() => {
                          // Separate templates into tenant and global
                          const tenantTemplates = templates.filter(
                            (template) => template.tenantId && template.tenantId !== '0' && template.tenantId === userTenantId
                          );
                          const globalTemplates = templates.filter(
                            (template) => template.tenantId === '0'
                          );

                          return (
                            <>
                              {tenantTemplates.length > 0 && (
                                <SelectGroup>
                                  {tenantTemplates.map((template) => (
                                    <SelectItem
                                      key={template.id}
                                      value={template.id}
                                      className="text-sm py-2.5"
                                    >
                                      {template.name}
                                    </SelectItem>
                                  ))}
                                </SelectGroup>
                              )}
                              {tenantTemplates.length > 0 && globalTemplates.length > 0 && (
                                <SelectSeparator />
                              )}
                              {globalTemplates.length > 0 && (
                                <SelectGroup>
                                  <SelectLabel>Global Templates</SelectLabel>
                                  {globalTemplates.map((template) => (
                                    <SelectItem
                                      key={template.id}
                                      value={template.id}
                                      className="text-sm py-2.5"
                                    >
                                      <div className="flex items-center gap-2">
                                        <span>{template.name}</span>
                                        <Badge variant="outline" className="text-xs">
                                          Global
                                        </Badge>
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectGroup>
                              )}
                            </>
                          );
                        })()}
                      </SelectContent>
                    </Select>
                    {validationErrors.template && (
                      <p className="text-xs text-red-600">{validationErrors.template}</p>
                    )}
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="flex-grow flex flex-col overflow-hidden">
                  <div className="grid grid-cols-2 gap-6 flex-grow overflow-hidden">
                    <div className="flex flex-col overflow-hidden">
                      <div className="mb-3 pb-2 border-b border-gray-200 flex-shrink-0">
                        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                          Available Pages
                        </h3>
                        <p className="text-xs text-gray-500 mt-1">Click to add to your room</p>
                      </div>
                      <div className="flex-grow overflow-y-auto pr-2 space-y-1.5">
                        {templatePages
                          .filter((page: TemplatePage) => !formData.selectedPages.includes(page.id))
                          .map((page: TemplatePage) => (
                            <Button
                              key={page.id}
                              variant="outline"
                              className="w-full justify-between h-auto py-2 px-3 hover:bg-blue-50 hover:border-blue-300 transition-all group"
                              onClick={() => handlePageToggle(page.id)}
                            >
                              <span className="font-medium text-gray-700 group-hover:text-blue-700">{page.title}</span>
                              {page.global && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className="cursor-help">
                                        <Globe className="h-4 w-4 text-blue-600" />
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent side="left" sideOffset={5}>
                                      <p className="max-w-xs">Global Page - This page is controlled by the template and cannot be edited</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </Button>
                          ))}
                      </div>
                    </div>
                    <div className="flex flex-col overflow-hidden">
                      <div className="mb-3 pb-2 border-b border-blue-200 bg-gradient-to-r from-blue-50 to-transparent rounded-t-lg px-3 py-2 -mx-3 -mt-1 flex-shrink-0">
                        <h3 className="font-semibold text-blue-900 flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full bg-blue-600"></div>
                          Selected Pages
                        </h3>
                        <p className="text-xs text-blue-600 mt-1">{formData.selectedPages.length} page(s) selected</p>
                      </div>
                      <div className="flex-grow overflow-y-auto pr-2 space-y-1.5">
                        {templatePages
                          .filter((page: TemplatePage) => formData.selectedPages.includes(page.id))
                          .sort((a: TemplatePage, b: TemplatePage) => a.order - b.order)
                          .map((page: TemplatePage) => (
                            <Button
                              key={page.id}
                              variant="secondary"
                              className="w-full justify-between h-auto py-2 px-3 bg-blue-50 border border-blue-200 hover:bg-blue-100 transition-all group"
                              onClick={() => handlePageToggle(page.id)}
                            >
                              <span className="font-medium text-blue-900">{page.title}</span>
                              {page.global && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <div className="cursor-help">
                                        <Globe className="h-4 w-4 text-blue-600" />
                                      </div>
                                    </TooltipTrigger>
                                    <TooltipContent side="left" sideOffset={5}>
                                      <p className="max-w-xs">Global Page - This page is controlled by the template and cannot be edited</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </Button>
                          ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}


            </div>

            <div className="mt-6 pt-4 border-t border-gray-200 shrink-0">
              <div className="flex justify-between gap-4">
                {step > 1 ? (
                  <Button
                    onClick={prevStep}
                    variant="outline"
                    className="w-full h-11 border-gray-300 hover:bg-gray-50 font-medium"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back
                  </Button>
                ) : (
                  <Button
                    onClick={onClose}
                    variant="outline"
                    className="w-full h-11 border-gray-300 hover:bg-gray-50 font-medium"
                  >
                    Cancel
                  </Button>
                )}
                {step < 2 ? (
                  <Button
                    onClick={nextStep}
                    disabled={!formData.account || !formData.template}
                    className="w-full h-11 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium shadow-lg shadow-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleFinish}
                    disabled={isLoading}
                    className="w-full h-11 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium shadow-lg shadow-blue-500/30 disabled:opacity-50"
                  >
                    {isLoading ? (
                      <>
                        <div className="h-4 w-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Creating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 mr-2" />
                        Create Room
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  )
}
